<template>
  <div>
    <v-tabs v-if="filteredSeries.length > 1 && displayMetas.length > 1">
      <v-tab v-for="(serie, i) in filteredSeries" :key="i">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ getTabTitleAbbreviation(serie.name) }}
            </span>
          </template>
          <span>
            {{ serie.name }}
          </span>
        </v-tooltip>
      </v-tab>

      <v-tab-item v-for="(serie, i) in filteredSeries" :key="i">
        <h4>
          {{ serie.name }}
        </h4>
        <div v-for="displayMeta in displayMetas" :key="displayMeta">
          <div class="overline primary--text pr-1">
            {{ getLegendTitle(displayMeta) }}
          </div>
          <p>{{ getLegendValue(i, displayMeta) }}</p>
        </div>
      </v-tab-item>
    </v-tabs>

    <div v-else-if="filteredSeries.length == 1">
      <h4>
        {{ filteredSeries.at(0)?.name ?? "N/A" }}
      </h4>
      <div v-for="displayMeta in displayMetas" :key="displayMeta">
        <div class="overline primary--text pr-1">
          {{ getLegendTitle(displayMeta) }}
        </div>
        <p>{{ getLegendValue(0, displayMeta) }}</p>
      </div>
    </div>
  </div>
</template>
<script>
import Meta from "@/_helpers/ModuleMeta";

const floatingPointErrorRounding = 1000000;

export default {
  name: "ChartLegend",

  props: {
    series: {
      Type: Array,
      default: [],
    },
    displayMetas: {
      Type: Array,
      default: [],
    },
    zoomProcent: {
      Type: Object,
      default: () => ({ start: 0, end: 100 }),
    },
  },

  data() {
    return {
      minimum: [],
      maximum: [],
      average: [],
      sum: [],
      integral: [],
    };
  },

  computed: {
    filteredSeries() {
      return this.series.filter(
        (x) => !x.name.startsWith(Meta.Enum.MOVING_AVERAGE_PREFIX)
      );
    },
  },

  methods: {
    getTabTitleAbbreviation(title) {
      let abbreviation = "";
      for (var subtitle of title.split("-")) {
        if (abbreviation != "") abbreviation += "-";
        subtitle.split(" ").forEach((x) => {
          abbreviation += x.charAt(0)?.toUpperCase() ?? "";
        });
      }
      return abbreviation;
    },

    getLegendTitle(title) {
      return (
        Meta.Keys.find((x) => x.key == title)
          ?.name?.split(" ")
          ?.at(1) ?? "Error"
      );
    },

    getLegendValue(serieIndex, title) {
      if (title == Meta.Enum.CHART_INTEGRAL)
        return this.integral.at(serieIndex) ?? "N/A";
      if (title == Meta.Enum.CHART_MINIMUM)
        return this.minimum.at(serieIndex) ?? "N/A";
      if (title == Meta.Enum.CHART_MAXIMUM)
        return this.maximum.at(serieIndex) ?? "N/A";
      if (title == Meta.Enum.CHART_SUM) return this.sum.at(serieIndex) ?? "N/A";
      if (title == Meta.Enum.CHART_AVERAGE) {
        let value = Number(this.average.at(serieIndex));
        if (isNaN(value)) return "N/A";
        let rounded = +value.toFixed(3);
        return value != rounded ? rounded + "..." : value;
      }
    },

    async calcValues() {
      //Index of a serie in filteredSeries will be used for direct access in those arrays so its important that they map to eachother correctly.
      this.minimum = [];
      this.maximum = [];
      this.average = [];
      this.sum = [];
      this.integral = [];
      if (this.displayMetas.length == 0) return;

      for (var serie of this.filteredSeries) {
        if (
          serie.data == null ||
          serie.data == undefined ||
          serie.data.length == 0
        ) {
          this.minimum.push("N/A");
          this.maximum.push("N/A");
          this.average.push("N/A");
          this.sum.push("N/A");
          this.integral.push("N/A");
          continue;
        }

        //Calc time interval
        let serieElapsedTime =
          new Date(serie.data[serie.data.length - 1].value.at(0)) -
          new Date(serie.data[0].value.at(0));

        let startTime =
          new Date(serie.data[0].value.at(0)).getTime() +
          (this.zoomProcent.start / 100) * serieElapsedTime;
        let endTime =
          new Date(serie.data[0].value.at(0)).getTime() +
          (this.zoomProcent.end / 100) * serieElapsedTime;

        let startIndex = serie.data.findIndex(
          (x) => new Date(x.value.at(0)).getTime() >= startTime
        );
        let end = serie.data.findIndex(
          (x) => new Date(x.value.at(0)).getTime() > endTime
        );

        //If end is undefined there is no datapoints beyond the zoom, hence we should set the end to be the length of the serie.data to include all values
        if (end == undefined || end == -1) end = serie.data.length;

        if (serieElapsedTime == undefined || startIndex == undefined) {
          this.minimum.push("N/A");
          this.maximum.push("N/A");
          this.average.push("N/A");
          this.sum.push("N/A");
          this.integral.push("N/A");
          continue;
        }

        let min = Number.MAX_VALUE;
        let max = Number.MIN_VALUE;
        let avrg = 0;
        let count = 0;
        let integralSum = serie.data.length > 1 ? 0 : "N/A";
        let numberParseError = false;

        //Calc min, max, sum and integral
        for (let i = startIndex; i < end; i++) {
          let x = serie.data[i].value.at(0);
          let y = Number(serie.data[i].value.at(1));
          count++;
          if (isNaN(y)) {
            numberParseError = true;
            continue;
          }

          if (min > y) min = y;
          if (max < y) max = y;
          avrg += y;

          if (i > 1) {
            let prevX = serie.data[i - 1].value.at(0);
            let prevY = Number(serie.data[i - 1].value.at(1));
            let deltaX = this.differenceInSeconds(x, prevX);

            if (isNaN(deltaX) || isNaN(prevY)) {
              numberParseError = true;
              continue;
            }
            //Trapezoidal_rule for integral
            integralSum += ((y + prevY) / 2) * deltaX;
          }
        }

        this.minimum.push(min);
        this.maximum.push(max);

        //Correct for floating point error when adding several numbers
        avrg =
          Math.round((avrg + Number.EPSILON) * floatingPointErrorRounding) /
          floatingPointErrorRounding;
        numberParseError ? this.sum.push("N/A") : this.sum.push(avrg);

        avrg /= count;
        numberParseError ? this.average.push("N/A") : this.average.push(avrg);

        //Correct for floating point error when adding several numbers
        integralSum =
          Math.round(
            (integralSum + Number.EPSILON) * floatingPointErrorRounding
          ) / floatingPointErrorRounding;

        numberParseError
          ? this.integral.push("N/A")
          : this.integral.push(integralSum);
      }
    },
  },

  async created() {
    await this.calcValues();
  },

  watch: {
    series: {
      async handler() {
        await this.calcValues();
      },
      deep: true,
    },
    zoomProcent: {
      async handler() {
        await this.calcValues();
      },
      deep: true,
    },
  },
};
</script>
